/**
 * Tier 1~4 검증 스테이지 (SPEC 구현 순서 8단계, `DEC-019` Tier 구성표).
 *
 * `region_id`는 `OPEN-07`의 기본값 `xx-00-proto`를 쓴다.
 * 모든 스테이지는 `validateStage`와 `validateStageSolvability`를 통과해야
 * 하며, `fixtures.test.ts`가 이를 강제한다.
 *
 * ## 면 컬러를 겹치게 둔 이유
 *
 * 여섯 면에 서로 다른 컬러를 주면 컬러만으로 면이 하나로 특정된다. 그러면
 * `color_and_shape` 모드에서 모양을 더해도 이미 특정된 면이 그대로라서
 * 난이도가 전혀 오르지 않는다 — 모양이 아무 정보도 싣지 않는다.
 *
 * 그래서 판정 모드마다 면 배치를 달리한다.
 * - `color` 모드(Tier 1·2): 3컬러 × 2면. 목표 컬러를 만족하는 면이 둘이라
 *   자유도가 크다.
 * - `color_and_shape` 모드(Tier 3·4): 2컬러 × 3모양. 쌍이 유일해 면이 하나로
 *   특정되고, 컬러만 봐서는 셋 중 하나까지만 좁혀진다. 모양을 봐야 한다는
 *   Tier 3의 학습 목표가 규칙으로 성립한다.
 *
 * 실측 `min_moves`는 9 → 19 → 21 → 32으로 단조 증가한다(`CR-02` §5 검증 항목).
 * Tier 2와 3은 같은 보드·같은 목표 셀을 쓰므로 그 사이의 +2는 순전히 판정
 * 속성이 늘어난 효과다(`DEC-019`의 계단식 구성).
 */

import type { RawStage } from './validate';

/** `color` 모드용 면 배치. 3컬러 × 2면. */
const COLOR_FACES: RawStage['faces'] = [
  { face_id: 'f0', color: 'orange' },
  { face_id: 'f1', color: 'orange' },
  { face_id: 'f2', color: 'sky' },
  { face_id: 'f3', color: 'sky' },
  { face_id: 'f4', color: 'green' },
  { face_id: 'f5', color: 'green' },
];

/** `color_and_shape` 모드용 면 배치. 2컬러 × 3모양, 여섯 쌍이 모두 다르다. */
const SHAPE_FACES: RawStage['faces'] = [
  { face_id: 'f0', color: 'orange', shape: 'circle' },
  { face_id: 'f1', color: 'orange', shape: 'square' },
  { face_id: 'f2', color: 'orange', shape: 'triangle' },
  { face_id: 'f3', color: 'blue', shape: 'circle' },
  { face_id: 'f4', color: 'blue', shape: 'square' },
  { face_id: 'f5', color: 'blue', shape: 'triangle' },
];

/**
 * 시작 칸. 모든 Tier가 원점에서 시작한다(`DEC-026`).
 *
 * 이 칸에는 **시작 시 바닥면과 조건이 맞는 타일**을 깔아 둔다. 플레이어는
 * 첫 화면에서 "무엇과 무엇이 같으면 열리는가"를 실물로 보고 출발한다.
 * 목표 타일이 아니므로 목표 수와 난이도 곡선(`DEC-019`, `DEC-032`)은
 * 그대로다 — 클리어 판정은 `is_target`인 타일만 본다.
 *
 * 기준 방향 상태의 바닥면은 면 1번이고, 두 면 배치 모두 그 자리가 주황이다.
 */
const START_CELL = { x: 0, y: 0 } as const;

/**
 * 장식 타일 — 목표가 아닌 표식 타일 (`DEC-051`).
 *
 * 목표만 놓인 판은 나머지가 전부 맨바닥이라, 무엇을 찾아야 하는지가 처음부터
 * 훤히 보인다. 표식이 있는 칸을 섞으면 목표를 **골라내는** 일이 생긴다.
 * 목표는 흰 테두리로 구분되므로 판정이 모호해지지는 않는다.
 *
 * 클리어 판정과 `min_moves`는 그대로다. 판정은 `is_target`인 타일만 보고,
 * solver 상태 키도 목표 타일의 개봉만 담기 때문이다(`DEC-043`). 그 결정이
 * 없었다면 장식 타일 네 장만으로도 탐색이 상한을 넘겼다.
 *
 * 이 타일들이 있어야 클리어 시 마무리 오픈(`DEC-038`)이 실제로 일한다.
 * 전에는 비목표 타일이 시작 칸 하나뿐이었고 그것은 0수에 이미 열려, 클리어
 * 시점에 닫힌 채 남는 타일이 없었다.
 *
 * Tier 2와 3은 **같은 자리**를 쓴다. 두 단계가 보드·목표·장식을 모두 공유해야
 * 그 사이의 차이가 판정 속성만의 효과로 남는다(`DEC-019`, `AC-22`).
 */
const TIER_1_DECOYS = [
  { x: 2, y: 1 },
  { x: 0, y: 2 },
  { x: 3, y: 3 },
] as const;

const TIER_2_3_DECOYS = [
  { x: 2, y: 0 },
  { x: 4, y: 2 },
  { x: 1, y: 2 },
  { x: 3, y: 4 },
  { x: 5, y: 3 },
  { x: 0, y: 4 },
] as const;

const TIER_4_DECOYS = [
  { x: 2, y: 1 },
  { x: 5, y: 4 },
  { x: 1, y: 3 },
  { x: 6, y: 2 },
  { x: 3, y: 6 },
  { x: 4, y: 0 },
  { x: 0, y: 4 },
  { x: 6, y: 5 },
  { x: 2, y: 7 },
  { x: 7, y: 3 },
] as const;

/** Tier 2와 Tier 3이 공유하는 목표 셀. 바뀌는 것을 판정 속성 하나로 묶는다. */
const TIER_2_3_CELLS = [
  { x: 5, y: 0 },
  { x: 0, y: 5 },
  { x: 5, y: 5 },
  { x: 2, y: 3 },
] as const;

/** Tier 1: 4×4, 목표 2개, 컬러 판정. 기본 굴림과 오픈을 익힌다. */
export const TIER_1_STAGE: RawStage = {
  stage_id: 'xx-00-proto-1-001',
  solid_type: 'cube',
  board_topology: 'square',
  board: { width: 4, height: 4 },
  match_mode: 'color',
  tier: 1,
  orientation_model: 'cube_24',
  seed: 'tier1-001',
  faces: COLOR_FACES,
  tiles: [
    { cell: START_CELL, color: 'orange', is_target: false },
    { cell: { x: 3, y: 0 }, color: 'sky', is_target: true },
    { cell: { x: 1, y: 3 }, color: 'green', is_target: true },
    { cell: TIER_1_DECOYS[0], color: 'green', is_target: false },
    { cell: TIER_1_DECOYS[1], color: 'sky', is_target: false },
    { cell: TIER_1_DECOYS[2], color: 'orange', is_target: false },
  ],
};

/** Tier 2: 6×6, 목표 4개, 컬러 판정. 보드 규모와 목표 수가 늘어난다. */
export const TIER_2_STAGE: RawStage = {
  stage_id: 'xx-00-proto-2-001',
  solid_type: 'cube',
  board_topology: 'square',
  board: { width: 6, height: 6 },
  match_mode: 'color',
  tier: 2,
  orientation_model: 'cube_24',
  seed: 'tier2-001',
  faces: COLOR_FACES,
  tiles: [
    { cell: START_CELL, color: 'orange', is_target: false },
    { cell: TIER_2_3_CELLS[0], color: 'sky', is_target: true },
    { cell: TIER_2_3_CELLS[1], color: 'green', is_target: true },
    { cell: TIER_2_3_CELLS[2], color: 'orange', is_target: true },
    { cell: TIER_2_3_CELLS[3], color: 'green', is_target: true },
    { cell: TIER_2_3_DECOYS[0], color: 'green', is_target: false },
    { cell: TIER_2_3_DECOYS[1], color: 'orange', is_target: false },
    { cell: TIER_2_3_DECOYS[2], color: 'sky', is_target: false },
    { cell: TIER_2_3_DECOYS[3], color: 'orange', is_target: false },
    { cell: TIER_2_3_DECOYS[4], color: 'green', is_target: false },
    { cell: TIER_2_3_DECOYS[5], color: 'sky', is_target: false },
  ],
};

/**
 * Tier 3: 6×6, 목표 4개, 컬러+모양 판정.
 * Tier 2와 보드·목표 셀이 같고 판정 속성만 늘어난다.
 */
export const TIER_3_STAGE: RawStage = {
  stage_id: 'xx-00-proto-3-001',
  solid_type: 'cube',
  board_topology: 'square',
  board: { width: 6, height: 6 },
  match_mode: 'color_and_shape',
  tier: 3,
  orientation_model: 'cube_24',
  seed: 'tier3-001',
  faces: SHAPE_FACES,
  tiles: [
    { cell: START_CELL, color: 'orange', shape: 'square', is_target: false },
    { cell: TIER_2_3_CELLS[0], color: 'orange', shape: 'triangle', is_target: true },
    { cell: TIER_2_3_CELLS[1], color: 'blue', shape: 'circle', is_target: true },
    { cell: TIER_2_3_CELLS[2], color: 'orange', shape: 'circle', is_target: true },
    { cell: TIER_2_3_CELLS[3], color: 'blue', shape: 'square', is_target: true },
    { cell: TIER_2_3_DECOYS[0], color: 'blue', shape: 'triangle', is_target: false },
    { cell: TIER_2_3_DECOYS[1], color: 'orange', shape: 'square', is_target: false },
    { cell: TIER_2_3_DECOYS[2], color: 'blue', shape: 'square', is_target: false },
    { cell: TIER_2_3_DECOYS[3], color: 'orange', shape: 'circle', is_target: false },
    { cell: TIER_2_3_DECOYS[4], color: 'blue', shape: 'circle', is_target: false },
    { cell: TIER_2_3_DECOYS[5], color: 'orange', shape: 'triangle', is_target: false },
  ],
};

/** Tier 4: 8×8, 목표 6개, 컬러+모양 판정. 보드 규모와 목표 수가 다시 늘어난다. */
export const TIER_4_STAGE: RawStage = {
  stage_id: 'xx-00-proto-4-001',
  solid_type: 'cube',
  board_topology: 'square',
  board: { width: 8, height: 8 },
  match_mode: 'color_and_shape',
  tier: 4,
  orientation_model: 'cube_24',
  seed: 'tier4-001',
  faces: SHAPE_FACES,
  tiles: [
    { cell: START_CELL, color: 'orange', shape: 'square', is_target: false },
    { cell: { x: 7, y: 0 }, color: 'orange', shape: 'triangle', is_target: true },
    { cell: { x: 0, y: 7 }, color: 'blue', shape: 'circle', is_target: true },
    { cell: { x: 7, y: 7 }, color: 'orange', shape: 'circle', is_target: true },
    { cell: { x: 3, y: 3 }, color: 'blue', shape: 'square', is_target: true },
    { cell: { x: 5, y: 1 }, color: 'orange', shape: 'square', is_target: true },
    { cell: { x: 1, y: 5 }, color: 'blue', shape: 'triangle', is_target: true },
    { cell: TIER_4_DECOYS[0], color: 'orange', shape: 'circle', is_target: false },
    { cell: TIER_4_DECOYS[1], color: 'blue', shape: 'square', is_target: false },
    { cell: TIER_4_DECOYS[2], color: 'blue', shape: 'triangle', is_target: false },
    { cell: TIER_4_DECOYS[3], color: 'orange', shape: 'square', is_target: false },
    { cell: TIER_4_DECOYS[4], color: 'orange', shape: 'triangle', is_target: false },
    { cell: TIER_4_DECOYS[5], color: 'blue', shape: 'circle', is_target: false },
    { cell: TIER_4_DECOYS[6], color: 'orange', shape: 'circle', is_target: false },
    { cell: TIER_4_DECOYS[7], color: 'blue', shape: 'triangle', is_target: false },
    { cell: TIER_4_DECOYS[8], color: 'orange', shape: 'square', is_target: false },
    { cell: TIER_4_DECOYS[9], color: 'blue', shape: 'circle', is_target: false },
  ],
};

/** Tier 순서대로 나열한 검증 스테이지. 화면의 단계 선택이 이 순서를 따른다. */
export const PROTOTYPE_STAGES: readonly RawStage[] = Object.freeze([
  TIER_1_STAGE,
  TIER_2_STAGE,
  TIER_3_STAGE,
  TIER_4_STAGE,
]);
